/**
 * features/textbooks/textbookStore.ts
 * ------------------------------------------------------------------
 * Reads and writes for Textbook Issue & Returns. The rules each write must
 * satisfy are in firestore.rules ("TEXTBOOKS") and lib/textbooks.ts.
 *
 * Each issue and each return is its own batch — the loan and its copy lock
 * together — so one pupil's failure (a copy already out) names that pupil and
 * does not take the rest of the class down with it.
 *
 * Charging is the one write to money. It reuses New Invoice's assembly and
 * numbering exactly, and commits the invoice with the loan's 'charged' mark in
 * ONE batch under a deterministic id: see HIGH-RISK-CHANGES.md §17.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import {
  collection, doc, getDoc, onSnapshot, query, where, writeBatch,
} from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import { stripUndefined } from '@/lib/sanitize';
import {
  TEXTBOOK_COLLECTIONS as TC, chargeInvoiceId, chargeLine, copyDocId, copyKey,
  type BookCondition, type Textbook, type TextbookLoan,
} from '@/lib/textbooks';
import {
  FINANCE_COLLECTIONS, assembleInvoice, formatInvoiceNo,
  type FinanceSettings, type Invoice,
} from '@/lib/financeCore';
import type { Student } from '@/types';

const now = () => new Date().toISOString();
export interface Actor { uid: string; name: string }

/* -------------------------------- reads -------------------------------- */

function useSnap<T>(q: ReturnType<typeof query> | null, key: string): { rows: T[]; ready: boolean; err: string } {
  const [state, setState] = useState<{ rows: T[]; ready: boolean; err: string }>({ rows: [], ready: false, err: '' });
  useEffect(() => {
    if (!q) { setState({ rows: [], ready: true, err: '' }); return; }
    setState((s) => ({ ...s, ready: false }));
    return onSnapshot(
      q,
      (snap) => setState({ rows: snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as T), ready: true, err: '' }),
      (e) => setState({ rows: [], ready: true, err: e.message }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

/** The whole shared book list, active and retired. */
export function useBooks() {
  return useSnap<Textbook>(query(collection(db, TC.books)), 'books');
}

/**
 * Loans still needing attention: out, or lost/ruined. History (returned) is
 * only loaded when asked for — it grows every year and most screens never
 * need it.
 */
export function useOpenLoans(enabled = true) {
  const out = useSnap<TextbookLoan>(
    enabled ? query(collection(db, TC.loans), where('status', '==', 'out')) : null, `out:${enabled}`);
  const gone = useSnap<TextbookLoan>(
    enabled ? query(collection(db, TC.loans), where('status', 'in', ['lost', 'unusable'])) : null, `gone:${enabled}`);
  return {
    rows: [...out.rows, ...gone.rows],
    ready: out.ready && gone.ready,
    err: out.err || gone.err,
  };
}

export function useReturnedLoans(enabled: boolean) {
  return useSnap<TextbookLoan>(
    enabled ? query(collection(db, TC.loans), where('status', '==', 'returned')) : null, `ret:${enabled}`);
}

/** One pupil's loans — the parent's and the pupil's own view. */
export function usePupilLoans(studentId: string | undefined) {
  return useSnap<TextbookLoan>(
    studentId ? query(collection(db, TC.loans), where('studentId', '==', studentId)) : null, `pupil:${studentId ?? ''}`);
}

/* ------------------------------- the list ------------------------------ */

export async function saveBook(
  book: Omit<Textbook, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName' | 'active'> & { active?: boolean },
  actor: Actor,
  existing?: Textbook,
): Promise<string> {
  const ref = existing ? doc(db, TC.books, existing.id) : doc(collection(db, TC.books));
  // A caller may pass a whole Textbook back (retiring one); its bookkeeping
  // fields are set below, never copied from the argument.
  const { id: _id, createdAt: _ca, updatedAt: _ua, createdBy: _cb, createdByName: _cn, ...fields } =
    book as Partial<Textbook>;
  await setDoc(ref, {
    ...fields,
    active: book.active ?? existing?.active ?? true,
    createdBy: existing?.createdBy ?? actor.uid,
    createdByName: existing?.createdByName ?? actor.name,
    createdAt: existing?.createdAt ?? now(),
    updatedAt: now(),
  });
  return ref.id;
}

/* ------------------------------- issuing ------------------------------- */

export interface IssueRow {
  student: Student;
  className: string;
  form?: string;
  copyNo: string;
  condition: BookCondition;
}

/**
 * Issue one copy to one pupil. Throws a sentence a teacher can act on when
 * the copy is already out — read from the lock first, because the batch alone
 * would only say "permission denied".
 */
export async function issueOne(book: Textbook, row: IssueRow, dueDate: string, actor: Actor): Promise<void> {
  const key = copyKey(row.copyNo);
  if (!key) throw new Error('No copy number.');
  const lockRef = doc(db, TC.copies, copyDocId(book.id, key));
  const lock = await getDoc(lockRef);
  if (lock.exists()) {
    const holder = lock.data().studentName as string | undefined;
    throw new Error(`Copy ${row.copyNo.trim()} is already out${holder ? ` to ${holder}` : ''}.`);
  }
  const loanRef = doc(collection(db, TC.loans));
  const loan: Omit<TextbookLoan, 'id'> = {
    bookId: book.id,
    title: book.title,
    isbn: book.isbn,
    subjectId: book.subjectId,
    subjectName: book.subjectName,
    copyNo: row.copyNo.trim(),
    copyKey: key,
    studentId: row.student.id,
    studentName: row.student.fullName,
    classId: row.student.currentClass,
    className: row.className,
    form: row.form,
    status: 'out',
    conditionOut: row.condition,
    issuedAt: now(),
    issuedBy: actor.uid,
    issuedByName: actor.name,
    dueDate: dueDate || undefined,
    replacementCost: book.replacementCost,
    updatedAt: now(),
  };
  const b = writeBatch(db);
  b.set(loanRef, stripUndefined(loan as unknown as Record<string, unknown>));
  b.set(lockRef, { loanId: loanRef.id, studentId: row.student.id, studentName: row.student.fullName });
  await b.commit();
}

/* ------------------------------ returning ------------------------------ */

export async function returnLoan(
  loan: TextbookLoan,
  outcome: { status: 'returned'; condition: BookCondition } | { status: 'lost' | 'unusable' },
  actor: Actor,
): Promise<void> {
  const b = writeBatch(db);
  b.update(doc(db, TC.loans, loan.id), stripUndefined({
    status: outcome.status,
    conditionIn: outcome.status === 'returned' ? outcome.condition : outcome.status,
    bursary: outcome.status === 'returned' ? undefined : 'pending',
    returnedAt: now(),
    returnedBy: actor.uid,
    returnedByName: actor.name,
    updatedAt: now(),
  }));
  b.delete(doc(db, TC.copies, copyDocId(loan.bookId, loan.copyKey)));
  await b.commit();
}

/** Undo a mistaken issue while the book is still out. */
export async function undoIssue(loan: TextbookLoan): Promise<void> {
  const b = writeBatch(db);
  b.delete(doc(db, TC.loans, loan.id));
  b.delete(doc(db, TC.copies, copyDocId(loan.bookId, loan.copyKey)));
  await b.commit();
}

/* ------------------------------ the bursar ----------------------------- */

export async function waiveLoan(loan: TextbookLoan, reason: string, actor: Actor): Promise<void> {
  await updateDoc(doc(db, TC.loans, loan.id), {
    bursary: 'waived',
    waiveReason: reason.trim(),
    bursaryAt: now(),
    bursaryBy: actor.uid,
    bursaryByName: actor.name,
    updatedAt: now(),
  });
}

/**
 * Charge the family for a lost or ruined copy: one manual invoice, built the
 * way New Invoice builds one, committed with the loan's 'charged' mark.
 * Returns the invoice so the caller can audit and email it.
 */
export async function chargeLoan(args: {
  loan: TextbookLoan;
  amount: number;
  student: Student | undefined;
  year: string;
  term: string;
  settings: FinanceSettings;
  invoicesThisYear: number;
  dueDate: string;
  actor: Actor;
}): Promise<Invoice> {
  const { loan, amount, student, year, term, settings, invoicesThisYear, dueDate, actor } = args;
  const line = chargeLine(loan, amount);
  const id = chargeInvoiceId(year, term, loan.studentId, loan.id);
  const invoiceNo = formatInvoiceNo(settings, { year, term: term as Invoice['term'], seq: invoicesThisYear + 1 });
  const inv = assembleInvoice({
    id, invoiceNo,
    studentId: loan.studentId,
    studentName: student?.fullName ?? loan.studentName,
    familyId: student?.familyId,
    term: term as Invoice['term'], year,
    issuedAt: now(),
    dueDate: new Date(dueDate).toISOString(),
    lines: [line],
    rules: [],
    status: 'issued',
  });
  const b = writeBatch(db);
  b.set(doc(db, FINANCE_COLLECTIONS.invoices, id), stripUndefined(inv as unknown as Record<string, unknown>));
  b.update(doc(db, TC.loans, loan.id), {
    bursary: 'charged',
    charge: { invoiceId: id, invoiceNo, amount: line.amount },
    bursaryAt: now(),
    bursaryBy: actor.uid,
    bursaryByName: actor.name,
    updatedAt: now(),
  });
  await b.commit();
  return inv;
}
